<template>
    <b-modal
        id="medsync-details-modal"
        ref="modal"
        title="MedSync Program Details"
        size="xl"
        hide-footer
        @hidden="onHidden">
        <div v-if="loading" class="text-center py-5">
            <b-spinner label="Loading..."></b-spinner>
            <div class="mt-2">Loading MedSync details...</div>
        </div>

        <div v-else-if="enrollment">
            <!-- Enrollment Summary -->
            <b-card class="mb-3" border-variant="primary">
                <b-row>
                    <b-col md="3">
                        <strong>Next Anchor Date:</strong><br>
                        <span class="text-primary">{{ formatDate(enrollment.anchorDate) }}</span>
                    </b-col>
                    <b-col md="3">
                        <strong>Cycle Length:</strong><br>
                        {{ enrollment.cycleDays }} days
                    </b-col>
                    <b-col md="3">
                        <strong>Medications in Sync:</strong><br>
                        {{ medications.length }}
                    </b-col>
                    <b-col md="3">
                        <strong>Status:</strong><br>
                        <b-badge :variant="enrollment.isActive ? 'success' : 'secondary'">
                            {{ enrollment.isActive ? 'Active' : 'Inactive' }}
                        </b-badge>
                    </b-col>
                </b-row>
            </b-card>

            <!-- Synchronized Medications Table -->
            <h5 class="mb-3"><i class="fa fa-pills mr-2"></i>Synchronized Medications</h5>
            <b-table
                v-if="medications.length > 0"
                :items="medications"
                :fields="medicationFields"
                striped
                hover
                >
                <template #cell(rxID)="data">
                    <strong>{{ data.value }}</strong>
                </template>

                <template #cell(drugName)="data">
                    {{ data.value }}
                </template>

                <template #cell(lastFillDate)="data">
                    {{ formatDate(data.value) }}
                </template>

                <template #cell(nextFillDate)="data">
                    {{ formatDate(data.value) }}
                </template>

                <template #cell(daySupply)="data">
                    {{ data.value || 'N/A' }}
                </template>

                <template #cell(refills)="data">
                    {{ data.value !== null ? data.value : 'N/A' }}
                </template>

                <template #cell(status)="data">
                    <b-badge :variant="getStatusVariant(data.value)">
                        {{ data.value }}
                    </b-badge>
                </template>
            </b-table>

            <div v-else class="text-center text-muted py-4">
                <i class="fa fa-info-circle fa-2x mb-2"></i>
                <p>No medications currently synchronized in this MedSync program.</p>
            </div>

            <!-- Contact History Section -->
            <hr class="my-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
                <h5 class="mb-0"><i class="fa fa-history mr-2"></i>Contact History</h5>
                <b-button
                    variant="primary"
                    size="sm"
                    @click="showAddContact = !showAddContact">
                    <i class="fa fa-plus"></i> Log Contact
                </b-button>
            </div>

            <!-- Add Contact Form -->
            <b-collapse v-model="showAddContact" class="mb-3">
                <b-card>
                    <b-form @submit.prevent="saveContact">
                        <b-row>
                            <b-col md="6">
                                <b-form-group label="Contact Status" label-for="contact-status">
                                    <b-form-select
                                        id="contact-status"
                                        v-model="contactForm.contactStatus"
                                        :options="contactStatusOptions"
                                        required>
                                    </b-form-select>
                                </b-form-group>
                            </b-col>
                            <b-col md="6">
                                <b-form-group label="Notes" label-for="contact-notes">
                                    <AutoCompleteTextarea
                                        id="contact-notes"
                                        v-model="contactForm.notes"
                                        rows="3"
                                        placeholder="Enter any notes about this contact attempt..."
                                    />
                                </b-form-group>
                            </b-col>
                        </b-row>
                        <b-button type="submit" variant="primary" size="sm" :disabled="saving">
                            <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                            {{ saving ? 'Saving...' : 'Save Contact' }}
                        </b-button>
                        <b-button variant="link" size="sm" @click="showAddContact = false">
                            Cancel
                        </b-button>
                    </b-form>
                </b-card>
            </b-collapse>

            <!-- Contact History Table -->
            <b-table
                v-if="contactHistory.length > 0"
                :items="contactHistory"
                :fields="contactFields"
                striped
                hover
                small>
                <template #cell(contactedDate)="data">
                    {{ formatDateTime(data.value) }}
                </template>

                <template #cell(contactStatus)="data">
                    <b-badge variant="info">{{ data.value }}</b-badge>
                </template>

                <template #cell(notes)="data">
                    <span class="text-muted">{{ data.value || '-' }}</span>
                </template>
            </b-table>

            <div v-else class="text-center text-muted py-3">
                <small>No contact history recorded.</small>
            </div>

            <!-- Close Button -->
            <div class="text-right mt-3">
                <b-button variant="secondary" @click="close">
                    Close
                </b-button>
            </div>
        </div>

        <div v-else class="text-center text-muted py-5">
            <i class="fa fa-exclamation-circle fa-2x mb-2"></i>
            <p>No MedSync enrollment found for this patient.</p>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import AutoCompleteTextarea from '@/components/AutoCompleteTextarea.vue';
import axios from 'axios';
import moment from 'moment';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { MedSyncEnrollment, MedSyncMedication, MedSyncContactHistory } from "@/models/MedSync";

@Component({
    components: { AutoCompleteTextarea },
})
export default class MedSyncDetailsModal extends Vue {
    @Prop({ required: true }) patientId!: number;

    private enrollment: MedSyncEnrollment | null = null;
    protected medications: MedSyncMedication[] = [];
    protected contactHistory: MedSyncContactHistory[] = [];
    private loading = false;
    private saving = false;
    private showAddContact = false;

    private contactForm = {
        contactStatus: '',
        notes: '',
    };

    private medicationFields = [
        { key: 'rxID', label: 'Rx ID', sortable: true },
        { key: 'drugName', label: 'Medication', sortable: true },
        { key: 'lastFillDate', label: 'Last Fill', sortable: true },
        { key: 'nextFillDate', label: 'Next Fill', sortable: true },
        { key: 'daySupply', label: 'Days Supply', sortable: true, thClass: 'text-left', tdClass: 'text-left' },
        { key: 'refills', label: 'Refills', sortable: true, thClass: 'text-left', tdClass: 'text-left' },
        { key: 'status', label: 'Status', sortable: true },
    ];

    private contactFields = [
        { key: 'contactedDate', label: 'Date/Time', sortable: true },
        { key: 'contactedBy', label: 'Contacted By', sortable: true },
        { key: 'contactStatus', label: 'Status', sortable: true },
        { key: 'notes', label: 'Notes' },
    ];

    private contactStatusOptions = [
        { value: '', text: 'Select status...' },
        { value: 'Contacted', text: 'Successfully Contacted' },
        { value: 'Left Message', text: 'Left Message' },
        { value: 'No Answer', text: 'No Answer' },
        { value: 'Wrong Number', text: 'Wrong Number' },
        { value: 'Patient Declined', text: 'Patient Declined' },
        { value: 'Other', text: 'Other' },
    ];

    async show() {
        (this.$refs.modal as any).show();
        await this.loadDetails();
    }

    close() {
        (this.$refs.modal as any).hide();
    }

    onHidden() {
        this.enrollment = null;
        this.medications = [];
        this.contactHistory = [];
        this.showAddContact = false;
        this.resetContactForm();
    }

    async loadDetails() {
        this.loading = true;
        try {
            // Load enrollment details
            const enrollmentResponse = await axios.get(`/api/medsync/enrollment/${this.patientId}`);

            if (enrollmentResponse.data.enrolled) {
                this.enrollment = enrollmentResponse.data.enrollment;

                const medsRequest = axios.get(`/api/medsync/enrollment/${this.enrollment!.id}/medications`);
                const historyRequest = axios.get(`/api/medsync/enrollment/${this.enrollment!.id}/contact-history`);

                // Load medications
                const medsResponse = await medsRequest;
                this.medications = medsResponse.data;

                // Load contact history
                const historyResponse = await historyRequest;
                this.contactHistory = historyResponse.data;
            }
        } catch (error) {
            console.error('Error loading MedSync details:', error);
            this.$notification(NotificationOptions.error('Failed to load MedSync details'));
        } finally {
            this.loading = false;
        }
    }

    async saveContact() {
        if (!this.contactForm.contactStatus || !this.enrollment) return;

        this.saving = true;
        try {
            await axios.post(`/api/medsync/enrollment/${this.enrollment.id}/contact`, {
                contactStatus: this.contactForm.contactStatus,
                notes: this.contactForm.notes,
            });

            this.$notification(NotificationOptions.success('Contact logged successfully'));
            this.showAddContact = false;
            this.resetContactForm();

            // Reload contact history
            const historyResponse = await axios.get(
                `/api/medsync/enrollment/${this.enrollment.id}/contact-history`
            );
            this.contactHistory = historyResponse.data;
        } catch (error) {
            console.error('Error saving contact:', error);
            this.$notification(NotificationOptions.error('Failed to log contact'));
        } finally {
            this.saving = false;
        }
    }

    resetContactForm() {
        this.contactForm = {
            contactStatus: '',
            notes: '',
        };
    }

    formatDate(date: Date | string | null): string {
        if (!date) return 'N/A';
        return moment(date).format('MM/DD/YYYY');
    }

    formatDateTime(date: Date | string | null): string {
        if (!date) return 'N/A';
        return moment(date).format('MM/DD/YYYY h:mm A');
    }

    getStatusVariant(status: string): string {
        const statusLower = status?.toLowerCase() || '';
        if (statusLower.includes('active') || statusLower.includes('refillable')) return 'success';
        if (statusLower.includes('hold') || statusLower.includes('pending')) return 'warning';
        if (statusLower.includes('void') || statusLower.includes('expired')) return 'danger';
        return 'secondary';
    }
}
</script>

<style scoped>
.card {
    border-radius: 0.25rem;
}

/* Add consistent spacing between sections */
h5 {
    margin-top: 0.5rem;
}

/* Improve modal body padding */
::v-deep .modal-body {
    padding: 1.25rem;
}

/* Add spacing between summary card columns on mobile */
@media (max-width: 767px) {
    .b-col {
        margin-bottom: 0.75rem;
    }
}
</style>
